import React, { useState, useEffect } from 'react';
import { Eye, ThumbsUp, Star } from 'lucide-react';
import { dealStatsApi } from '../services/dealStatsApi';
import { toast } from 'sonner';

const DealStatsInteractive = ({ dealId, initialViews = 0 }) => {
  const [stats, setStats] = useState({
    views: initialViews,
    likes: 0,
    average_rating: 0,
    total_ratings: 0,
  });
  const [isLiked, setIsLiked] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      const data = await dealStatsApi.getStats(dealId);
      if (data) {
        setStats(data);
      }
      setIsLoading(false);
    };
    fetchStats();

    // Check if user has liked this deal (from localStorage)
    const likedDeals = JSON.parse(localStorage.getItem('likedDeals') || '[]');
    setIsLiked(likedDeals.includes(dealId));

    // Check if user has rated this deal
    const ratedDeals = JSON.parse(localStorage.getItem('ratedDeals') || '{}');
    if (ratedDeals[dealId]) {
      setUserRating(ratedDeals[dealId]);
    }
  }, [dealId]);

  // Increment view when component mounts (once per session)
  useEffect(() => {
    const viewedDeals = JSON.parse(sessionStorage.getItem('viewedDeals') || '[]');
    if (!viewedDeals.includes(dealId)) {
      dealStatsApi.incrementView(dealId).then((data) => {
        if (data) {
          setStats((prev) => ({ ...prev, views: data.views }));
        }
      });
      sessionStorage.setItem('viewedDeals', JSON.stringify([...viewedDeals, dealId]));
    }
  }, [dealId]);

  const handleLike = async () => {
    const likedDeals = JSON.parse(localStorage.getItem('likedDeals') || '[]');
    
    if (isLiked) {
      // Already liked - show message
      toast.info('You already liked this deal!');
      return;
    }

    const data = await dealStatsApi.likeDeal(dealId);
    if (data) {
      setStats((prev) => ({ ...prev, likes: data.likes }));
      setIsLiked(true);
      localStorage.setItem('likedDeals', JSON.stringify([...likedDeals, dealId]));
      toast.success('Deal liked!');
    }
  };

  const handleRate = async (rating) => {
    const ratedDeals = JSON.parse(localStorage.getItem('ratedDeals') || '{}');
    
    if (ratedDeals[dealId]) {
      toast.info('You already rated this deal!');
      return;
    }

    const data = await dealStatsApi.rateDeal(dealId, rating);
    if (data) {
      setStats((prev) => ({
        ...prev,
        average_rating: data.average_rating,
        total_ratings: data.total_ratings,
      }));
      setUserRating(rating);
      ratedDeals[dealId] = rating;
      localStorage.setItem('ratedDeals', JSON.stringify(ratedDeals));
      toast.success(`Rated ${rating} stars!`);
    }
  };

  const renderStars = () => {
    const stars = [];
    const displayRating = hoverRating || userRating || stats.average_rating;
    
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          onClick={() => handleRate(i)}
          onMouseEnter={() => setHoverRating(i)}
          onMouseLeave={() => setHoverRating(0)}
          className={`transition-all duration-150 ${userRating ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          disabled={!!userRating}
        >
          <Star
            size={16}
            className={`${
              i <= displayRating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300 hover:text-yellow-300'
            } transition-colors`}
          />
        </button>
      );
    }
    return stars;
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-4 text-gray-400 text-xs animate-pulse">
        <span className="flex items-center gap-1">
          <Eye size={14} />
          <span className="w-8 h-3 bg-gray-200 rounded"></span>
        </span>
        <span className="flex items-center gap-1">
          <ThumbsUp size={14} />
          <span className="w-6 h-3 bg-gray-200 rounded"></span>
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      {/* Views */}
      <span className="flex items-center gap-1 text-gray-500">
        <Eye size={14} />
        <span>{stats.views.toLocaleString()}</span>
      </span>

      {/* Likes */}
      <button
        onClick={handleLike}
        className={`flex items-center gap-1 transition-all duration-200 ${
          isLiked 
            ? 'text-black' 
            : 'text-gray-500 hover:text-black'
        }`}
      >
        <ThumbsUp 
          size={14} 
          className={isLiked ? 'fill-black' : ''} 
        />
        <span>{stats.likes.toLocaleString()}</span>
      </button>

      {/* Rating Stars */}
      <div className="flex items-center gap-1">
        <div className="flex">
          {renderStars()}
        </div>
        <span className="text-gray-500 ml-1">
          ({stats.average_rating.toFixed(1)})
        </span>
        {stats.total_ratings > 0 && (
          <span className="text-gray-400 text-[10px]">
            {stats.total_ratings} {stats.total_ratings === 1 ? 'rating' : 'ratings'}
          </span>
        )}
      </div>
    </div>
  );
};

export default DealStatsInteractive;
